import React, { useRef, useEffect } from 'react';
import './AIModeDropdown.css';

/**
 * Desktop AI Mode Selector Component (Dropdown)
 * Supports both Agent and Plan modes with a dropdown interface
 * 
 * @param {Object} props
 * @param {string} props.aiMode - Current AI mode ('agent' or 'plan')
 * @param {Function} props.onModeChange - Callback when mode changes
 * @param {boolean} props.isOpen - Whether dropdown is open
 * @param {Function} props.onToggle - Callback to toggle dropdown
 * @param {boolean} props.isLoading - Whether AI is currently processing
 */
function AIModeDropdown({ aiMode, onModeChange, isOpen, onToggle, isLoading }) {
  const dropdownRef = useRef(null);
  
  const modes = [
    {
      value: 'agent',
      label: 'Agent',
      description: 'AI can create projects and code',
      icon: (
        <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
          <rect x="3" y="11" width="18" height="10" rx="2"/>
          <circle cx="12" cy="5" r="2"/>
          <path d="M12 7v4M8 16h.01M16 16h.01"/>
        </svg>
      )
    },
    {
      value: 'plan',
      label: 'Plan',
      description: 'Provides information and planning only',
      icon: (
        <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
          <path d="M9 12h6M9 16h6"/>
        </svg>
      )
    }
  ];

  const currentMode = modes.find(m => m.value === aiMode) || modes[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onToggle(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen, onToggle]);

  const handleModeSelect = (mode) => {
    // Track AI mode change
    if (window.analytics?.trackAction) {
      window.analytics.trackAction('chat', 'ai_mode_changed', { 
        mode,
        previousMode: aiMode
      });
    }
    onModeChange(mode);
    onToggle(false);
  };

  return (
    <div className="ai-mode-dropdown-container" ref={dropdownRef}>
      <button
        className={`ai-mode-toggle ${isOpen ? 'open' : ''} ${isLoading ? 'icon-only' : ''}`}
        disabled={isLoading}
        onClick={() => onToggle(!isOpen)}
        title={currentMode.description}
        type="button"
      >
        {currentMode.icon}
        <span className="button-text">{currentMode.label}</span>
        <svg 
          className={`dropdown-arrow ${isOpen ? 'open' : ''}`}
          width="12" 
          height="12" 
          viewBox="0 0 12 12"
        >
          <path 
            d="M3 4.5l3 3 3-3" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="ai-mode-dropdown-menu">
          {modes.map((mode) => (
            <button
              key={mode.value}
              className={`ai-mode-option ${aiMode === mode.value ? 'active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleModeSelect(mode.value);
              }}
              type="button"
            >
              <div className="ai-mode-option-content">
                {mode.icon}
                <div className="ai-mode-option-text">
                  <span className="ai-mode-option-label">{mode.label}</span>
                  <span className="ai-mode-option-desc">{mode.description}</span>
                </div>
              </div>
              {aiMode === mode.value && (
                <svg 
                  className="ai-mode-check"
                  width="16" 
                  height="16" 
                  viewBox="0 0 16 16"
                >
                  <path 
                    d="M13 4l-7 7-3-3" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default AIModeDropdown;
